import React from 'react'
import { Sheet, Stepper } from '@sendoutcards/quantum-design-ui'
import {
  useAccount,
  useActions,
  useMutations,
  usePersistedState,
  useQueryParams,
  useState,
  useSubscriptions,
  useVertical,
} from 'src/hooks'
import { compact } from 'src/helpers'
import TrialAffiliateProfile from '../progressive-profiling/TrialAffiliateProfile'
import FreeCardNotification from '../free-card-notification/FreeCardNotification'
import GracePeriodNotification from '../grace-period-notification/GracePeriodNotification'
import {
  Label,
  OverridableUserLabel,
  UserLabelOverrideAction,
} from 'src/graphql'
import ProgramOptIn, { ActionKeys } from './ProgramOptIn'
import NonActionStep, { ContentType } from './NonActionStep'

const lastActionsReviewTimestampKey = 'last_actions_review_timestamp'

const hasBeen24Hours = (lastActionsReviewTimestamp?: string) => {
  if (!lastActionsReviewTimestamp) {
    return true
  }
  const initialTimeStamp = parseInt(lastActionsReviewTimestamp, 10)
  const now = Date.now() / 1000
  const twentyFourHours = 86400
  return now - initialTimeStamp > twentyFourHours
}

const ProgressiveProfileActions = () => {
  const account = useAccount()
  const vertical = useVertical()
  const queryParams = useQueryParams()
  const mutations = useMutations()
  const actions = useActions()
  const [
    lastActionsReviewTimestamp,
    setLastActionsReviewTimestamp,
  ] = usePersistedState<string>(lastActionsReviewTimestampKey, '')
  const [canContinue, setCanContinue] = useState(false)
  const [activeItemIndex, setActiveItemIndex] = useState(0)
  const [affiliateOptInResponse, setAffiliateOptInResponse] = useState('')
  const [custOptInResponse, setCustOptInResponse] = useState('')
  const [isSheetOpen, setIsSheetOpen] = useState(true)
  const { isInGreenerStillGracePeriod } = useSubscriptions()

  const onStepsComplete = async () => {
    setLastActionsReviewTimestamp((Date.now() / 1000).toString())
    // Affiliate or Customer optIn
    if (
      affiliateOptInResponse === ActionKeys.optIn ||
      custOptInResponse === ActionKeys.optIn
    ) {
      const {
        createDistributor: { account: updatedAccount, error },
      } = await mutations.createAffiliate({
        account: { username: account.username },
        govId1: '',
        affiliate: affiliateOptInResponse === ActionKeys.optIn,
        verticalId: vertical.graphqlVerticalID,
        queryParams,
      })
      if (updatedAccount) {
        actions.updatedAccount(updatedAccount)
      } else if (error) {
        console.log(error)
      }
    }

    // Affiliate optOut
    if (affiliateOptInResponse === ActionKeys.optOut) {
      mutations.updateUserLabelOverrides({
        overrides: [
          {
            label: OverridableUserLabel.NEEDS_GS_AFFILIATE_OPT_IN,
            action: UserLabelOverrideAction.EXCLUDE,
          },
        ],
      })
    }

    // Customer optOut
    if (custOptInResponse === ActionKeys.optOut) {
      mutations.updateUserLabelOverrides({
        overrides: [
          {
            label: OverridableUserLabel.NEEDS_CUSTOMER_REFERRAL_OPT_IN,
            action: UserLabelOverrideAction.EXCLUDE,
          },
        ],
      })
    }
  }
  const completionMessage = 'Setup Complete!'

  const isMissingTaxForm =
    account.labels.includes(Label.AFFILIATE_MISSING_W8) ||
    account.labels.includes(Label.AFFILIATE_MISSING_W9)

  const children = compact(
    account.isQualifiedForFirstFreeCard && (
      <FreeCardNotification
        onComplete={() => setCanContinue(true)}
        dismissModal={() => setIsSheetOpen(false)}
      />
    ),
    account.labels.includes(Label.NEEDS_CUSTOMER_REFERRAL_OPT_IN) && (
      <ProgramOptIn
        message="customerReferralOptInMessage"
        onComplete={() => setCanContinue(true)}
        setResponse={setCustOptInResponse}
      />
    ),
    account.isTrialAffiliate && !account.isQualifiedForFirstFreeCard && (
      <TrialAffiliateProfile onComplete={() => setCanContinue(true)} />
    ),
    account.labels.includes(Label.NEEDS_GS_AFFILIATE_OPT_IN) && (
      <ProgramOptIn
        message="greenerStillAffiliateOptInMessage"
        onComplete={() => setCanContinue(true)}
        setResponse={setAffiliateOptInResponse}
      />
    ),
    isMissingTaxForm && (
      <NonActionStep
        content={ContentType.TaxInfo}
        onComplete={() => setCanContinue(true)}
      />
    ),
    account.labels.includes(Label.AFFILIATE_RECEIVES_PHYSICAL_CHECKS) && (
      <NonActionStep
        content={ContentType.PayoutMethod}
        onComplete={() => setCanContinue(true)}
      />
    ),
    isInGreenerStillGracePeriod && (
      <GracePeriodNotification
        onComplete={() => setCanContinue(true)}
        dismissModal={() => setIsSheetOpen(false)}
      />
    ),
  )

  if (children.length === 0 || !hasBeen24Hours(lastActionsReviewTimestamp)) {
    return null
  }

  return (
    <Sheet isOpen={isSheetOpen}>
      <Stepper
        canContinue={canContinue}
        setCanContinue={setCanContinue}
        activeItemIndex={activeItemIndex}
        setActiveItemIndex={setActiveItemIndex}
        onStepsComplete={onStepsComplete}
        completionMessage={completionMessage}
      >
        {children}
      </Stepper>
    </Sheet>
  )
}

export default ProgressiveProfileActions
